import React from 'react'
import { IoClose } from 'react-icons/io5'
import { PiMagnifyingGlass } from 'react-icons/pi'
import { PiFunnel } from 'react-icons/pi'
import { IoMdArrowUp, IoMdArrowDown } from 'react-icons/io'
import { SortIcon } from '../Icons/SortIcon'
import { IconButton } from './MobileSearch.styles'

// Sort option labels (must match MobileSearchSort options)
const SORT_LABELS: Record<
  string,
  { label: string; arrow: 'up' | 'down' | null }
> = {
  relevancy: { label: 'Relevancy', arrow: null },
  alphabetical_asc: { label: 'Alphabetical', arrow: 'up' },
  alphabetical_desc: { label: 'Alphabetical', arrow: 'down' },
  size_desc: { label: 'Company Size', arrow: 'down' },
  size_asc: { label: 'Company Size', arrow: 'up' },
}

interface MobileSearchInputProps {
  value: string
  onChange: (value: string) => void
  onClear: () => void
  onFilterClick: () => void
  onSortClick: () => void
  currentSort?: string
  isSortOpen?: boolean
  placeholder?: string
}

export const MobileSearchInput: React.FC<MobileSearchInputProps> = ({
  value,
  onChange,
  onClear,
  onFilterClick,
  onSortClick,
  currentSort = 'relevancy',
  isSortOpen = false,
  placeholder = 'Search...',
}) => {
  const sortOption = SORT_LABELS[currentSort] || SORT_LABELS.relevancy
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Blur the input to dismiss the keyboard
      e.currentTarget.blur()
    }
  }

  const handleFilterClick = () => {
    // Dismiss keyboard before opening filter panel
    inputRef.current?.blur()
    onFilterClick()
  }

  const handleSortClick = () => {
    // Dismiss keyboard before opening sort panel
    inputRef.current?.blur()
    onSortClick()
  }

  return (
    <div className="relative flex items-center gap-2">
      <div className="relative flex-1">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-new-company-gray-500">
          <PiMagnifyingGlass size={20} />
        </div>
        <input
          ref={inputRef}
          type="text"
          enterKeyHint="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          className="w-full px-10 py-3 border border-new-company-gray-border rounded-lg text-sm text-new-company-gray-900 placeholder:text-new-company-gray-500 focus:outline-none focus:border-fb-green-900"
        />
        {value && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-0 text-new-company-gray-500 cursor-pointer p-1 flex items-center hover:text-new-company-gray-900"
          >
            <IoClose size={20} />
          </button>
        )}
      </div>

      <IconButton
        type="button"
        onClick={handleFilterClick}
        aria-label="Filter results"
        title="Filter"
      >
        <PiFunnel size={20} />
      </IconButton>

      <IconButton
        type="button"
        onClick={handleSortClick}
        aria-label="Sort results"
        title="Sort"
      >
        {isSortOpen ? (
          <div className="flex items-center gap-2">
            <span className="text-sm font-normal text-new-company-gray-900 flex items-center gap-0.5 whitespace-nowrap">
              {sortOption.label}
              {sortOption.arrow === 'up' && <IoMdArrowUp size={14} />}
              {sortOption.arrow === 'down' && <IoMdArrowDown size={14} />}
            </span>
            <div className="w-px h-5 bg-new-company-gray-border" />
            <SortIcon size={20} />
          </div>
        ) : (
          <SortIcon size={20} />
        )}
      </IconButton>
    </div>
  )
}
